<template>
    <div
        class="best-cards-widget col-span-12 lg:col-span-6 bg-white dark:bg-slate-800 overflow-hidden shadow dark:shadow-slate-900/50 rounded-lg md:min-h-[400px]"
    >
        <div class="p-6 h-full">
            <!-- Header (only when has cards) -->
            <div
                v-if="!loading && showContent"
                class="flex justify-between items-center"
            >
                <div class="flex items-center gap-2">
                    <Icon icon="card-check" class="w-5 h-5 text-slate-800 dark:text-slate-200" />
                    <span class="text-lg font-medium text-slate-800 dark:text-slate-200">
                        My best cards
                    </span>
                </div>
                <a
                    href="/wallet/best-cards"
                    class="link-base link-dark flex items-center text-sm font-medium text-slate-800 dark:text-slate-200 hover:text-slate-700 dark:hover:text-slate-300"
                >
                    View all
                    <Icon icon="arrow" />
                </a>
            </div>

            <!-- Loading State -->
            <div
                v-if="loading"
                class="relative h-64 flex items-center justify-center"
            >
                <LoadingState
                    :loading="true"
                    size="large"
                    :noBackground="true"
                />
            </div>

            <!-- Content with cards -->
            <div v-else-if="showContent" class="mt-4">
                <!-- Category Pills Carousel -->
                <div class="relative mb-6">
                    <div
                        ref="pillsContainer"
                        class="flex gap-2 overflow-x-auto scrollbar-hide pr-8 py-1 pl-1"
                    >
                        <button
                            v-for="cat in categories"
                            :key="cat.category_key"
                            @click="selectCategory(cat)"
                            :class="[
                                'flex items-center gap-2 px-3 py-1.5 rounded-full border whitespace-nowrap transition-all shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1',
                                selectedCategory?.category_key ===
                                cat.category_key
                                    ? 'bg-slate-800 dark:bg-slate-700 text-white shadow border-slate-800 dark:border-slate-600 hover:bg-slate-700 dark:hover:bg-slate-600 active:bg-slate-800 focus:ring-slate-600'
                                    : 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-300 active:bg-slate-100 focus:ring-slate-400',
                            ]"
                        >
                            <Icon :icon="cat.category_icon" class="w-4 h-4" />
                            <span class="text-sm font-medium">
                                {{ cat.category_name }}
                            </span>
                        </button>
                    </div>
                    <!-- Fade gradient on right -->
                    <div
                        class="absolute right-0 top-0 bottom-0 w-12 pointer-events-none gradient-fade"
                    ></div>
                </div>

                <!-- Selected Card Display -->
                <div v-if="selectedCategory" class="flex flex-col items-center">
                    <!-- Card Image (clickable) -->
                    <button
                        @click="openCardModal"
                        class="w-full max-w-[240px] mb-4 cursor-pointer hover:opacity-90 transition-opacity rounded-lg"
                    >
                        <div
                            class="aspect-[1.586/1] rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 shadow-md"
                        >
                            <img
                                v-if="selectedCardImageUrl"
                                :src="selectedCardImageUrl"
                                :alt="selectedCategory.best_card.card_title"
                                class="w-full h-full object-cover"
                            />
                            <div
                                v-else
                                class="w-full h-full flex items-center justify-center"
                            >
                                <Icon
                                    icon="credit-card"
                                    class="w-12 h-12 text-slate-300"
                                />
                            </div>
                        </div>
                    </button>

                    <!-- Card Info (clickable) -->
                    <button
                        @click="openCardModal"
                        @mouseenter="
                            isTitleTruncated && (showTitleTooltip = true)
                        "
                        @mouseleave="showTitleTooltip = false"
                        class="text-center focus:outline-none w-full relative"
                    >
                        <!-- Tooltip -->
                        <Transition name="fade">
                            <div
                                v-if="showTitleTooltip"
                                class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-lg bg-slate-800 text-white text-sm whitespace-normal max-w-[280px] z-50 shadow-lg"
                            >
                                {{ selectedCategory.best_card.card_title }}
                                <div
                                    class="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800"
                                ></div>
                            </div>
                        </Transition>
                        <h3
                            class="text-base font-semibold text-center whitespace-nowrap"
                        >
                            <span class="text-slate-800 dark:text-slate-100">
                                {{ truncatedWidgetTitle }}
                            </span>
                            <span
                                v-if="selectedCategory.best_card.last_four"
                                class="text-slate-400 dark:text-slate-500 font-normal"
                            >
                                ({{ selectedCategory.best_card.last_four }})
                            </span>
                        </h3>
                        <p class="text-orange font-semibold text-sm mt-1">
                            {{ selectedCategory.best_card.multiplier_display }}
                        </p>
                    </button>
                </div>
            </div>

            <!-- Promo State (no cards) -->
            <div v-else class="flex flex-col items-center gap-4 py-4">
                <img
                    src="/images/graphics-best-card.png"
                    alt="Best Cards Promo"
                    class="max-w-[280px]"
                />
                <div class="flex flex-col items-center gap-2 w-full">
                    <h2 class="text-center text-[30px] font-semibold leading-9">
                        <span class="text-orange">Maximize rewards</span>
                        <br />
                        <span class="text-slate-800 dark:text-slate-100">with every purchase</span>
                    </h2>
                    <p
                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center max-w-[320px]"
                    >
                        Add your cards and we'll help you choose the best card
                        for every purchase.
                    </p>
                </div>
                <a href="/wallet/best-cards" class="btn-base btn-10x-orange">
                    View My Best Cards
                </a>
            </div>
        </div>

        <!-- Card Info Modal -->
        <CardInfoModal
            :isOpen="isModalOpen"
            :card="selectedCategory?.best_card"
            :category="modalCategory"
            @close="closeCardModal"
            @view-in-wallet="goToWallet"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import CardInfoModal from '@/components/wallet/best-cards-by-category/card-info-modal.vue';

const props = defineProps({
    initialCategories: {
        type: Array,
        default: () => [],
    },
});

const loading = ref(false);
const rawCategories = ref(props.initialCategories);

const selectedCategory = ref(null);
const pillsContainer = ref(null);
const isModalOpen = ref(false);
const titleRef = ref(null);
const showTitleTooltip = ref(false);
const isTitleTruncated = ref(false);

// Max characters for 1 line minus space for digits
const MAX_WIDGET_TITLE_CHARS = 60;

const truncatedWidgetTitle = computed(() => {
    const title = selectedCategory.value?.best_card?.card_title || '';
    const hasLastFour = !!selectedCategory.value?.best_card?.last_four;
    // Reserve space for " (XXXX)" = 7 chars if has last_four
    const maxChars = hasLastFour
        ? MAX_WIDGET_TITLE_CHARS - 7
        : MAX_WIDGET_TITLE_CHARS;

    if (title.length <= maxChars) {
        isTitleTruncated.value = false;
        return title;
    }
    isTitleTruncated.value = true;
    return title.substring(0, maxChars).trim() + '...';
});

const cdnUrl = computed(
    () => import.meta.env.VITE_DO_CDN_ENDPOINT || window.cdnUrl
);

// Filter out empty categories (no best_card)
const categories = computed(() =>
    rawCategories.value.filter(
        (cat) => cat.best_card && cat.best_card.card_title
    )
);

const showContent = computed(() => categories.value.length > 0);

const selectedCardImageUrl = computed(() => {
    const img = selectedCategory.value?.best_card?.card_image;
    if (img?.path && img?.file_name) {
        return `${cdnUrl.value}/${img.path}/_small/${img.file_name}`;
    }
    return null;
});

const selectCategory = (cat) => {
    selectedCategory.value = cat;
};

const modalCategory = computed(() => {
    if (!selectedCategory.value) return null;
    return {
        name: selectedCategory.value.category_name,
        icon: selectedCategory.value.category_icon,
    };
});

const openCardModal = () => {
    isModalOpen.value = true;
};

const closeCardModal = () => {
    isModalOpen.value = false;
};

const goToWallet = () => {
    const connectedAccountId =
        selectedCategory.value?.best_card?.connected_account_id;
    closeCardModal();
    if (connectedAccountId) {
        router.visit(`/wallet?card_id=${connectedAccountId}&open_sidebar=true`);
    } else {
        router.visit('/wallet');
    }
};

const fetchCategories = async () => {
    if (rawCategories.value.length > 0) {
        if (categories.value.length > 0) {
            selectedCategory.value = categories.value[0];
        }
        return;
    }

    loading.value = true;
    try {
        const { data } = await axios.get('/api/wallets/best-cards');
        rawCategories.value = data.categories || [];
        if (categories.value.length > 0) {
            selectedCategory.value = categories.value[0];
        }
    } catch (error) {
        console.error('Failed to fetch best cards:', error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchCategories();
});
</script>

<style scoped>
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.gradient-fade {
    background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
}
:where(.dark) .gradient-fade {
    background: linear-gradient(270deg, #1e293b 0%, rgba(30, 41, 59, 0) 100%);
}

.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
